import { getConfigBoolean } from '@config'
import Logger from '@logger'
import type { ILatLng } from '@shared_types/global_types'
import { isValidGeolocation } from 'is-valid-geolocation'
import { isBoolean } from 'lodash'
import type { Moment } from 'moment'
import moment from 'moment-timezone'
import { getSunrise, getSunset } from 'sunrise-sunset-js'
import tzAbbreviations from 'timezone-abbreviations'

const logger = Logger.create('TimeUtils')

export const timezoneCodes = new Map<string, string>()

for (const a of tzAbbreviations) {
  for (const name of a.names || []) {
    timezoneCodes.set(name, a.abbr)
  }
}

// missing in 'timezone-abbreviations' package
timezoneCodes.set('Africa/Casablanca', 'WEST')
timezoneCodes.set('Africa/El_Aaiun', 'WEST')
timezoneCodes.set('America/Araguaina', 'BRT')
timezoneCodes.set('America/Argentina/Buenos_Aires', 'ART')
timezoneCodes.set('America/Argentina/Catamarca', 'ART')
timezoneCodes.set('America/Argentina/ComodRivadavia', 'ART')
timezoneCodes.set('America/Argentina/Cordoba', 'ART')
timezoneCodes.set('America/Argentina/Jujuy', 'ART')
timezoneCodes.set('America/Argentina/La_Rioja', 'ART')
timezoneCodes.set('America/Argentina/Mendoza', 'ART')
timezoneCodes.set('America/Argentina/Rio_Gallegos', 'ART')
timezoneCodes.set('America/Argentina/Salta', 'ART')
timezoneCodes.set('America/Argentina/San_Juan', 'ART')
timezoneCodes.set('America/Argentina/San_Luis', 'ART')
timezoneCodes.set('America/Argentina/Tucuman', 'ART')
timezoneCodes.set('America/Argentina/Ushuaia', 'ART')
timezoneCodes.set('America/Asuncion', 'PYT')
timezoneCodes.set('America/Bahia', 'BRT')
timezoneCodes.set('America/Bele', 'BRT')
timezoneCodes.set('America/Belem', 'BRT')
timezoneCodes.set('America/Boa_Vista', 'AMT')
timezoneCodes.set('America/Bogota', 'COT')
timezoneCodes.set('America/Buenos_Aires', 'AMT')
timezoneCodes.set('America/Campo_Grande', 'AMT')
timezoneCodes.set('America/Caracas', 'VET')
timezoneCodes.set('America/Catamarca', 'ART')
timezoneCodes.set('America/Cayenne', 'GFT')
timezoneCodes.set('America/Cordoba', 'AMT')
timezoneCodes.set('America/Cuiaba', 'BRT')
timezoneCodes.set('America/Eirunepe', 'ACT')
timezoneCodes.set('America/Fortaleza', 'BRT')
timezoneCodes.set('America/Godthab', 'BRT')
timezoneCodes.set('America/Guayaquil', 'ECT')
timezoneCodes.set('America/Guyana', 'GYT')
timezoneCodes.set('America/Jujuy', 'AMT')
timezoneCodes.set('America/La_Paz', 'BOT')
timezoneCodes.set('America/Lima', 'PET')
timezoneCodes.set('America/Maceio', 'BRT')
timezoneCodes.set('America/Manaus', 'AMT')
timezoneCodes.set('America/Mendoza', 'AMT')
timezoneCodes.set('America/Miquelon', 'PMST')
timezoneCodes.set('America/Montevideo', 'UYT')
timezoneCodes.set('America/Noronha', 'FNT')
timezoneCodes.set('America/Paramaribo', 'SRT')
timezoneCodes.set('America/Porto_Acre', 'ACT')
timezoneCodes.set('America/Porto_Velho', 'AMT')
timezoneCodes.set('America/Punta_Arenas', 'CLST')
timezoneCodes.set('America/Recife', 'BRT')
timezoneCodes.set('America/Rio_Branco', 'ACT')
timezoneCodes.set('America/Rosario', 'ART')
timezoneCodes.set('America/Santarem', 'BRT')
timezoneCodes.set('America/Santiago', 'CLT')
timezoneCodes.set('America/Sao_Paulo', 'BRT')
timezoneCodes.set('America/Scoresbysund', 'EGST')
timezoneCodes.set('Antarctica/Casey', 'CAST')
timezoneCodes.set('Antarctica/Davis', 'DAVT')
timezoneCodes.set('Antarctica/DumontDUrville', 'DDUT')
timezoneCodes.set('Antarctica/Mawson', 'MAWT')
timezoneCodes.set('Antarctica/Palmer', 'CLST')
timezoneCodes.set('Antarctica/Rothera', 'ART')
timezoneCodes.set('Antarctica/Syowa', 'SYOT')
timezoneCodes.set('Antarctica/Troll', 'CEST')
timezoneCodes.set('Antarctica/Vostok', 'VOST')
timezoneCodes.set('Asia/Aden', 'AST')
timezoneCodes.set('Asia/Almaty', 'ALMT')
timezoneCodes.set('Asia/Anadyr', 'ANAT')
timezoneCodes.set('Asia/Aqtau', 'MSD+1')
timezoneCodes.set('Asia/Aqtobe', 'AQTT')
timezoneCodes.set('Asia/Ashgabat', 'TMT')
timezoneCodes.set('Asia/Ashkhabad', 'TMT')
timezoneCodes.set('Asia/Atyrau', 'MSD+1')
timezoneCodes.set('Asia/Baghdad', 'AST')
timezoneCodes.set('Asia/Bahrain', 'AST')
timezoneCodes.set('Asia/Baku', 'AZT')
timezoneCodes.set('Asia/Bangkok', 'ICT')
timezoneCodes.set('Asia/Barnaul', 'MSK+4')
timezoneCodes.set('Asia/Bishkek', 'KGST')
timezoneCodes.set('Asia/Brunei', 'BNT')
timezoneCodes.set('Asia/Chita', 'YAKT')
timezoneCodes.set('Asia/Choibalsan', 'CHOT')
timezoneCodes.set('Asia/Colombo', 'IST')
timezoneCodes.set('Asia/Dacca', 'BST')
timezoneCodes.set('Asia/Dhaka', 'BST')
timezoneCodes.set('Asia/Dili', 'TLT')
timezoneCodes.set('Asia/Dubai', 'GST')
timezoneCodes.set('Asia/Dushanbe', 'TJT')
timezoneCodes.set('Asia/Ho_Chi_Minh', 'IDT')
timezoneCodes.set('Asia/Hovd', 'HOVT')
timezoneCodes.set('Asia/Irkutsk', 'IRKT')
timezoneCodes.set('Asia/Istanbul', 'TRT')
timezoneCodes.set('Asia/Kabul', 'AFT')
timezoneCodes.set('Asia/Kamchatka', 'PETT')
timezoneCodes.set('Asia/Kashgar', 'CST')
timezoneCodes.set('Asia/Kathmandu', 'NPT')
timezoneCodes.set('Asia/Katmandu', 'NPT')
timezoneCodes.set('Asia/Khandyga', 'YAKT')
timezoneCodes.set('Asia/Krasnoyarsk', 'KRAT')
timezoneCodes.set('Asia/Kuala_Lumpur', 'MYT')
timezoneCodes.set('Asia/Kuching', 'MYT')
timezoneCodes.set('Asia/Kuwait', 'AST')
timezoneCodes.set('Asia/Magadan', 'MAGT')
timezoneCodes.set('Asia/Muscat', 'GST')
timezoneCodes.set('Asia/Novokuznetsk', 'KRAT')
timezoneCodes.set('Asia/Novosibirsk', 'NOVT')
timezoneCodes.set('Asia/Omsk', 'OMST')
timezoneCodes.set('Asia/Oral', 'ORAST')
timezoneCodes.set('Asia/Phnom_Penh', 'ICT')
timezoneCodes.set('Asia/Qatar', 'AST')
timezoneCodes.set('Asia/Qostanay', 'QYZST')
timezoneCodes.set('Asia/Qyzylorda', 'MSK+2')
timezoneCodes.set('Asia/Rangoon', 'MMT')
timezoneCodes.set('Asia/Riyadh', 'AST')
timezoneCodes.set('Asia/Saigon', 'ICT')
timezoneCodes.set('Asia/Sakhalin', 'SAKT')
timezoneCodes.set('Asia/Samarkand', 'UZT')
timezoneCodes.set('Asia/Singapore', 'SST')
timezoneCodes.set('Asia/Srednekolymsk', 'SRET')
timezoneCodes.set('Asia/Tashkent', 'UZT')
timezoneCodes.set('Asia/Tbilisi', 'GET')
timezoneCodes.set('Asia/Tehran', 'IRST')
timezoneCodes.set('Asia/Thimbu', 'BTT')
timezoneCodes.set('Asia/Thimphu', 'BTT')
timezoneCodes.set('Asia/Tomsk', 'MSD+3')
timezoneCodes.set('Asia/Ulaanbaatar', 'ULAT')
timezoneCodes.set('Asia/Ulan_Bator', 'ULAT')
timezoneCodes.set('Asia/Urumqi', 'URUT')
timezoneCodes.set('Asia/Ust-Nera', 'VLAT')
timezoneCodes.set('Asia/Vientiane', 'ICT')
timezoneCodes.set('Asia/Vladivostok', 'VLAT')
timezoneCodes.set('Asia/Yakutsk', 'YAKT')
timezoneCodes.set('Asia/Yangon', 'MMT')
timezoneCodes.set('Asia/Yekaterinburg', 'YEKT')
timezoneCodes.set('Asia/Yerevan', 'AMT')
timezoneCodes.set('Atlantic/Azores', 'AZOST')
timezoneCodes.set('Atlantic/Cape_Verde', 'CVT')
timezoneCodes.set('Atlantic/South_Georgia', 'GST')
timezoneCodes.set('Atlantic/Stanley', 'FKT')
timezoneCodes.set('Australia/Eucla', 'ACWST')
timezoneCodes.set('Australia/LHI', 'LHST')
timezoneCodes.set('Australia/Lord_Howe', 'LHST')
timezoneCodes.set('Brazil/Acre', 'ACT')
timezoneCodes.set('Brazil/DeNoronha', 'FNT')
timezoneCodes.set('Brazil/East', 'BRT')
timezoneCodes.set('Brazil/West', 'BRT')
timezoneCodes.set('Chile/Continental', 'CLT')
timezoneCodes.set('Chile/EasterIsland', 'CLT')
timezoneCodes.set('Europe/Astrakhan', 'MSK+1')
timezoneCodes.set('Europe/Istanbul', 'TRT')
timezoneCodes.set('Europe/Kirov', 'MSK')
timezoneCodes.set('Europe/Minsk', 'MSK')
timezoneCodes.set('Europe/Samara', 'SAMT')
timezoneCodes.set('Europe/Saratov', 'MSD')
timezoneCodes.set('Europe/Ulyanovsk', 'MSK+1')
timezoneCodes.set('Europe/Volgograd', 'VOLT')
timezoneCodes.set('Indian/Chagos', 'IOT')
timezoneCodes.set('Indian/Christmas', 'CXT')
timezoneCodes.set('Indian/Cocos', 'CCT')
timezoneCodes.set('Indian/Kerguelen', 'TFT')
timezoneCodes.set('Indian/Mahe', 'SCT')
timezoneCodes.set('Indian/Maldives', 'MVT')
timezoneCodes.set('Indian/Mauritius', 'MUT')
timezoneCodes.set('Indian/Reunion', 'RET')
timezoneCodes.set('Iran', 'IRDT')
timezoneCodes.set('Kwajalein', '+12')
timezoneCodes.set('NZ-CHAT', 'NZ-CHAT')
timezoneCodes.set('Pacific/Apia', 'WST')
timezoneCodes.set('Pacific/Bougainville', 'BST')
timezoneCodes.set('Pacific/Chatham', 'CHAST')
timezoneCodes.set('Pacific/Chuuk', 'CHUT')
timezoneCodes.set('Pacific/Easter', 'EAST')
timezoneCodes.set('Pacific/Efate', 'VUT')
timezoneCodes.set('Pacific/Enderbury', 'PHOT')
timezoneCodes.set('Pacific/Fakaofo', 'TKT')
timezoneCodes.set('Pacific/Fiji', 'FJT')
timezoneCodes.set('Pacific/Funafuti', 'TVT')
timezoneCodes.set('Pacific/Galapagos', 'GALT')
timezoneCodes.set('Pacific/Gambier', 'GAMT')
timezoneCodes.set('Pacific/Guadalcanal', 'SBT')
timezoneCodes.set('Pacific/Kiritimati', 'LINT')
timezoneCodes.set('Pacific/Kosrae', 'KOST')
timezoneCodes.set('Pacific/Kwajalein', '+12')
timezoneCodes.set('Pacific/Majuro', '+12')
timezoneCodes.set('Pacific/Marquesas', 'MART')
timezoneCodes.set('Pacific/Nauru', 'NRT')
timezoneCodes.set('Pacific/Niue', 'NUT')
timezoneCodes.set('Pacific/Norfolk', 'NFT')
timezoneCodes.set('Pacific/Noumea', 'NCT')
timezoneCodes.set('Pacific/Palau', '+09')
timezoneCodes.set('Pacific/Pitcairn', 'PST')
timezoneCodes.set('Pacific/Pohnpei', 'PONT')
timezoneCodes.set('Pacific/Ponape', 'PONT')
timezoneCodes.set('Pacific/Port_Moresby', 'PGT')
timezoneCodes.set('Pacific/Rarotonga', 'CKT')
timezoneCodes.set('Pacific/Tahiti', 'TAHT')
timezoneCodes.set('Pacific/Tarawa', 'GILT')
timezoneCodes.set('Pacific/Tongatapu', 'TOT')
timezoneCodes.set('Pacific/Truk', 'WIB')
timezoneCodes.set('Pacific/Wake', 'WAKT')
timezoneCodes.set('Pacific/Wallis', 'WFT')
timezoneCodes.set('Pacific/Yap', 'CHUT')
timezoneCodes.set('Singapore', 'SGT')
timezoneCodes.set('Turkey', 'TUR')
timezoneCodes.set('US/Pacific-New', 'PST')

for (const name of moment.tz.names()) {
  if (!timezoneCodes.has(name) && !name.startsWith('Etc/')) {
    logger.verbose(`timezone not added: ${name} [skipped, name does not start with "Etc/"]`)
  }
}

export function getFormattedZoneName (name: string) {
  return name.slice(name.lastIndexOf('/') + 1, name.length).replace('_', ' ')
}

// A short UTC offset label for a moment, e.g. 'UTC+2', 'UTC-4', 'UTC+5:30'.
export function formatUtcOffsetLabel (m: Moment): string {
  const offset = m.utcOffset()
  const sign = offset < 0 ? '-' : '+'
  const abs = Math.abs(offset)
  const hours = Math.floor(abs / 60)
  const minutes = abs % 60
  return `UTC${sign}${hours}${minutes ? ':' + String(minutes).padStart(2, '0') : ''}`
}

export function is24HTimeSystem () {
  const is24Hour = getConfigBoolean('model.timeSystem.is24Hour')
  return isBoolean(is24Hour) ? is24Hour : true
}

export const getTimeFormat = () => (is24HTimeSystem() ? 'HH:mm' : 'hh:mm a')

export const isoStringToTimestamp = (isoString: Moment | Date | string | number | null) =>
  moment(isoString).toDate().getTime()

export function formatDateTime (date: Moment | Date | string | number | null, hours?: boolean, minutes?: boolean) {
  if (!date) {
    return date
  }

  let format

  const is24h = is24HTimeSystem()
  if (hours) {
    format = is24h ? 'HH' : 'hh'
  } else if (minutes) {
    format = is24h ? 'mm' : 'mm a'
  } else {
    format = getTimeFormat()
  }

  return moment(date).format(format)
}

export function getSunEventsForLocation (location: ILatLng, date = new Date()) {
  let sunrise: Date | null = null
  let sunset: Date | null = null

  if (isValidGeolocation(location)) {
    sunset = getSunset(location.lat, location.lng, date)
    sunrise = getSunrise(location.lat, location.lng, date)
  }

  return { sunrise, sunset }
}

export function getSunsetAndSunrise (pos: ILatLng, timeString: string) {
  return getSunEventsForLocation(pos, new Date(timeString))
}

export const defaultTimezones = [
  { zone: 'Europe/Moscow', coord: [55.7558, 37.6173] },
  { zone: 'America/New_York', coord: [40.435, -73.5668] },
  { zone: 'Europe/London', coord: [51.5074, 0.1278] },
  { zone: 'Europe/Amsterdam', coord: [52.3676, 4.9041] },
  { zone: 'Asia/Tokyo', coord: [35.6762, 139.6503] },
  { zone: 'Asia/Hong_Kong', coord: [22.3193, 114.1694] },
  { zone: 'Asia/Dubai', coord: [25.2048, 55.2708] },
  { zone: 'Atlantic/Reykjavik', coord: [64.1466, -21.9426] }
]

export function tsIsToday (ts: number | string | Moment) {
  return moment(ts).toDate().toDateString() === moment().toDate().toDateString()
}
